import { styled } from '@linaria/react';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { BrandedLoadingOverlay } from '../../../components/loader/branded-loader';
import { exportChart } from '../../../helpers/image-downloader/image-downloader';
import { ChartType } from '../../../store/financials-store/financials-store.type';
import { useWorkPeriodStore } from '../../../store/work-period-store/work-period-store';
import { WorkPeriodChartHeader } from '../components/work-period-chart-header.tsx';
import { useWorkPeriodTaskTypeBreakdown } from '../hooks/work-periods.hooks';
import { BreakdownControls } from './breakdown-controls';
import { buildBreakdownChartEntries } from './breakdown.helpers';

export const Breakdown = () => {
  const { t } = useTranslation();
  const { selectedWorkPeriods } = useWorkPeriodStore();
  const selectedWorkPeriod = useMemo(() => selectedWorkPeriods?.[0], [selectedWorkPeriods]);
  const { data, query } = useWorkPeriodTaskTypeBreakdown({
    workPeriodId: selectedWorkPeriod.id,
  });
  const chartRef = useRef<HTMLDivElement>(null!);
  const [selectedChartType, setSelectedChartType] = useState<ChartType>(ChartType.Pie);

  // Prefer the labeled type_breakdown rows (verbatim external casing) and
  // fall back to the legacy types map for older backends.
  const chartData = buildBreakdownChartEntries(data, t);

  const renderChart = () => {
    if (selectedChartType === ChartType.Pie) {
      return (
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              dataKey="count"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={100}
              fill="#8884d8"
              isAnimationActive={false}
              label={({ name, percent }) => `${name} (${(((percent as number) || 0) * 100).toFixed(0)}%)`}
            >
              {chartData.map((entry: any, index: any) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Pie>
            <Tooltip
              formatter={(value) => [`${value ?? 0} tasks`, 'Count']}
              labelFormatter={(name) => `Type: ${String(name)}`}
            />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      );
    }

    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis />
          <Tooltip
            formatter={(value) => [`${value ?? 0} tasks`, 'Count']}
            labelFormatter={(name) => `Type: ${String(name)}`}
          />
          <Legend />
          <Bar dataKey="count" name="Task Count" isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    );
  };

  return (
    <BreakdownContainer ref={chartRef}>
      <BrandedLoadingOverlay visible={query.isLoading} variant="colored" scoped />
      <WorkPeriodChartHeader
        title="Breakdown"
        selectedWorkPeriod={selectedWorkPeriod}
        onExport={() => exportChart(chartRef.current, 'sprint-composition-breakdown.png')}
        additionalControls={
          <BreakdownControls selectedChartType={selectedChartType} setSelectedChartType={setSelectedChartType} />
        }
        hideRadio
      />

      <ChartArea>
        <ChartContainer>{renderChart()}</ChartContainer>
      </ChartArea>
    </BreakdownContainer>
  );
};

const BreakdownContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const ChartArea = styled.div`
  flex: 1;
  min-height: 0;
`;

const ChartContainer = styled.div`
    height: calc(100% - 32px);
    margin: 16px 0;

    svg {
      border: none !important;
      outline: none !important;
    }

    .recharts-wrapper {
      border: none !important;
      outline: none !important;
    }

    .recharts-pie,
    .recharts-bar,
    .recharts-surface {
      border: none !important;
      outline: none !important;
    }
  `;
